/**
 * /admin/reports/registers — voucher registers launcher.
 * @file src/web/features/admin/pages/reports/registers.tsx
 *
 * One card per voucher type (Sales, Purchase, Journal, Payment, Receipt,
 * Contra, …) with its count — each opens the Day Book filtered to that
 * type, for the selected entity + period.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ListTree, ArrowRight } from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { AdminPageHeader } from '../../components/AdminPageHeader';
import { PageLoading } from '../../components/PageLoading';
import { adminFetchJson } from '../../lib/admin-api';
import { useCompany } from '../../lib/company-context';
import { usePeriod } from '../../lib/period-context';
import { PeriodBar } from '../../components/PeriodBar';

interface TypeRow { type: string; count: number }

export default function RegistersPage() {
  const { entityId } = useCompany();
  const { from, to } = usePeriod();
  const [types, setTypes] = useState<TypeRow[] | null>(null);

  useEffect(() => {
    let cancel = false; setTypes(null);
    adminFetchJson<{ items: TypeRow[] }>('/api/tally-import/vouchers/types', { query: { entityId: entityId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setTypes(d.items))
      .catch(() => !cancel && setTypes([]));
    return () => { cancel = true; };
  }, [entityId, from, to]);

  return (
    <>
      <SEO title="Registers" />
      <AdminPageHeader title="Registers" breadcrumbs={[{ label: 'Reports', href: '/admin/reports' }, { label: 'Registers' }]} />
      <Link to="/admin/reports" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700"><ChevronLeft className="h-4 w-4" /> All reports</Link>
      <PeriodBar />

      {!types && <PageLoading label="Loading registers" />}
      {types && types.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><ListTree className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">No vouchers</h3>
          <p className="mt-1.5 text-sm text-ink-600">Import a Day Book for this entity and period.</p>
        </div>
      )}
      {types && types.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {types.map((t) => (
            <Link key={t.type} to={`/admin/daybook?type=${encodeURIComponent(t.type)}`} className="group flex items-center justify-between rounded-2xl border border-ink-200 bg-paper p-5 no-underline transition hover:-translate-y-0.5 hover:border-highlighter hover:shadow-sm">
              <div>
                <p className="text-sm font-semibold text-ink-900">{t.type}</p>
                <p className="mt-1 text-xs text-ink-500">{t.count.toLocaleString('en-IN')} voucher{t.count === 1 ? '' : 's'}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-300 transition-transform group-hover:translate-x-0.5 group-hover:text-ink-500" />
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
